import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { DollarSign, CheckCircle, Clock, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const PatientPayments = () => {
  const [payments, setPayments] = useState([]);

  const fetchPayments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/payments`, { withCredentials: true });
      setPayments(data);
    } catch (error) {
      console.error('Error:', error);
    }
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const totalPending = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0);

  const statusConfig = {
    paid: { icon: CheckCircle, color: 'text-[#0F1B3D]', bg: 'bg-[#0F1B3D]/10', label: 'Pago' },
    pending: { icon: Clock, color: 'text-[#E9C46A]', bg: 'bg-[#E9C46A]/10', label: 'Pendente' },
    overdue: { icon: XCircle, color: 'text-[#A8174E]', bg: 'bg-[#A8174E]/10', label: 'Atrasado' },
    cancelled: { icon: XCircle, color: 'text-[#7A85A0]', bg: 'bg-[#7A85A0]/10', label: 'Cancelado' }
  };

  return (
    <div className="space-y-6" data-testid="patient-payments">
      <div>
        <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Meus Pagamentos</h2>
        <p className="text-[#5F6B85]">Acompanhe seus pagamentos</p>
      </div>

      {totalPending > 0 && (
        <Card className="border-[#E9C46A] bg-[#E9C46A]/5" data-testid="pending-total-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-[#E9C46A]" />
              <div>
                <p className="text-sm text-[#5F6B85]">Total Pendente</p>
                <p className="text-2xl font-semibold text-[#E9C46A]">R$ {totalPending.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {payments.length === 0 ? (
          <Card className="border-[#E5DCC8]">
            <CardContent className="p-8 text-center">
              <p className="text-[#7A85A0]">Nenhum pagamento encontrado</p>
            </CardContent>
          </Card>
        ) : (
          payments.map((payment) => {
            const config = statusConfig[payment.status] || statusConfig.pending;
            const Icon = config.icon;
            return (
              <Card key={payment.payment_id} className="border-[#E5DCC8]" data-testid={`patient-payment-${payment.payment_id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 ${config.bg} rounded-lg`}>
                        <Icon className={`h-5 w-5 ${config.color}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0F1B3D]">{payment.description}</p>
                        <p className="text-sm text-[#7A85A0]">
                          {new Date(payment.created_at).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-semibold text-[#0F1B3D]">R$ {(payment.amount || 0).toFixed(2)}</p>
                      <span className={`text-xs font-medium ${config.color}`}>{config.label}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
